import { type DefaultValueConverters } from '@databases/collab/default-value-converters';
import { YDatabase } from '@databases/collab/y-database';
import { collabStateManager } from '@databases/collab-state-manager';
import { getCloudId, getVersionId } from '@databases/database-state';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import { logError } from '@databases/logs';
import { UserType } from '@databases/model/value';
import { type Content } from '@databases/types/confluence';
import { type Database } from '@databases/types/database';
import { getBasicCollabDatabaseFromContent } from '@databases/utils/database/service';
import { CollabManager } from '@databases/utils/redux/collab/collab-manager';
import {
  convertToDatabase,
  convertToDatabaseFromDiff,
  type YJSDatabaseSubset,
} from '@databases/utils/redux/collab/convert-to-database';
import { applyDatabaseDiff } from '@databases/utils/redux/databases/actions';
import { type CommonRootState } from '@databases/utils/redux/redux';
import { generateId } from '@databases/utils/utils';
import { type AnyAction } from 'redux';
import { type ThunkDispatch, type ThunkAction } from 'redux-thunk';
import type * as Y from 'yjs';

import {
  currentHostFrameContext,
  getFrameContextReferenceId,
} from '@canvas/canvas-state/host-api';

type Dispatcher<Result> = ThunkAction<Result, CommonRootState, void, AnyAction>;

// TODO no redux dependencies, can move out of redux package
export const buildDefaultValueConverters = (): DefaultValueConverters => {
  return {
    getCurrentUser: () => {
      const currentUserId = currentBridgeData.data.identity.accountId;

      return {
        accountId: currentUserId,
        id: generateId(),
        type: UserType.Referenced,
      };
    },
  };
};

export const syncCollabChangesToRedux = (
  collabManager: CollabManager,
  dispatch: ThunkDispatch<CommonRootState, void, AnyAction>,
  options: {
    contentId: string;
    isReadOnly?: boolean;
  }
) => {
  return (
    update: Uint8Array,
    origin: unknown,
    doc: Y.Doc,
    transaction: Y.Transaction
  ) => {
    try {
      const { contentId, isReadOnly } = options;
      const collabDatabase = collabManager.database;
      const diff = collabDatabase.getDiff(transaction);

      dispatch(
        applyDatabaseDiff({
          contentId,
          diff,
          collabDatabase,
          isReadOnly: isReadOnly ?? collabManager.isReadOnly,
        })
      );
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch (e: unknown) {
      if (e instanceof Error) {
        // Capture exception and allow the user action to proceed
        logError(`Error applying database diff: ${e.message}`, e);
      }
    }
  };
};

// TODO no redux dependencies, can move out of redux package
export const createCollabManager = ({
  contentId,
  spaceId,
  referenceId,
  initialDatabase,
  cloudId,
  versionId,
}: {
  contentId: string;
  spaceId: number;
  referenceId: string;
  initialDatabase?: YDatabase;
  cloudId: string | undefined;
  versionId?: string;
}): CollabManager => {
  if (!cloudId) {
    throw new Error('cloudId is not defined');
  }

  const emptyDb =
    initialDatabase || new YDatabase(undefined, buildDefaultValueConverters());

  const token =
    getFrameContextReferenceId() === referenceId
      ? currentHostFrameContext.data
      : undefined;

  const collabManager = new CollabManager(
    cloudId,
    spaceId.toString(),
    contentId,
    referenceId,
    emptyDb,
    token,
    versionId
  );

  collabStateManager.setCollabManager(contentId, collabManager);

  return collabManager;
};

// TODO no redux dependencies, can move out of redux package
export const buildDatabaseFromCollabAndContent = (
  databaseContent: Content<'database'>,
  collabDatabase: YDatabase,
  isReadOnly: boolean,
  transaction?: Y.Transaction,
  baseDatabase?: YJSDatabaseSubset
) => {
  if (!databaseContent.referenceId) {
    throw new Error('missing referenceId');
  }

  const base = getBasicCollabDatabaseFromContent(databaseContent, isReadOnly);

  const collabDatabaseAsDatabase =
    transaction && baseDatabase
      ? convertToDatabaseFromDiff(
          collabDatabase.getDiff(transaction),
          collabDatabase,
          baseDatabase
        )
      : convertToDatabase(collabDatabase);

  const database: Database = {
    ...base,
    id: databaseContent.referenceId,
    referenceId: databaseContent.referenceId,

    metaVersion: 0,

    entriesContentId: '', // TODO: null this type
    entriesContentVersion: 0,

    ...collabDatabaseAsDatabase,
  };

  return database;
};

export const getOrCreateCollabManager =
  (
    {
      contentId,
      spaceId,
      referenceId,
      isInitialLoad = false,
    }: {
      contentId: string;
      spaceId: number;
      referenceId: string;
      isInitialLoad: boolean;
    },
    initialDatabase?: YDatabase
  ): Dispatcher<Promise<CollabManager>> =>
  async (dispatch) => {
    if (isInitialLoad) {
      collabStateManager.closeInactiveSessions(contentId);
    }

    const existingCollabManager = collabStateManager.getCollabManager(contentId);

    if (existingCollabManager) {
      return existingCollabManager;
    }

    if (!spaceId && !referenceId) {
      const error = new Error('Missing required parameters: spaceId and referenceId');

      logError(error);
      throw error;
    }

    const cloudId = getCloudId();
    const versionId = getVersionId();

    const collabManager = createCollabManager({
      contentId,
      spaceId,
      referenceId,
      initialDatabase,
      cloudId,
      versionId,
    });

    collabManager.onPermissionsChanged((isReadOnly) => {
      dispatch(
        applyDatabaseDiff({
          contentId,
          diff: {
            fieldsOrderingChanged: false,
            entriesOrderingChanged: false,
            changedFieldIds: [],
            changedEntryIds: [],
            threadsChanged: false,
            viewsChanged: false,
            defaultEntryChanged: false,
            othersChanged: false,
          },
          collabDatabase: collabManager.database,
          isReadOnly,
        })
      );
    });

    collabManager.database.doc.on(
      'update',
      syncCollabChangesToRedux(collabManager, dispatch, { contentId })
    );

    return collabManager;
  };
